import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useNavigate, useParams, NavLink } from 'react-router-dom';
import {
  Users,
  LayoutDashboard,
  Settings,
  LogOut,
  Bell,
  Search as SearchIcon,
  HelpCircle,
  Mail,
  FileText,
  Plane,
  Ticket,
  Activity,
  UserPlus,
} from 'lucide-react';
import type { AdminOutletContext } from '../types';
import { loadNotifications } from '../lib/skyluxStore';
import { useAgents } from '../context/AgentsContext';
import { useAuth } from '../context/AuthContext';
import { fetchFlights, fetchQuotes, fetchTickets } from '../lib/api';

type DashStats = {
  openTickets: number;
  totalTickets: number;
  unpaidQuotes: number;
  totalQuotes: number;
  flights: number;
  loading: boolean;
  error: string | null;
};

const AdminDashboard = () => {
  const { logout, username: authUsername } = useAuth();
  const navigate = useNavigate();
  const { agents, loading: agentsLoading, canManageAgents } = useAgents();
  const { agentId } = useParams();
  const effectiveAgentId = agentId ?? agents[0]?.id ?? '';
  const [globalSearch, setGlobalSearch] = useState('');
  const [storeTick, setStoreTick] = useState(0);
  const [dashStats, setDashStats] = useState<DashStats>({
    openTickets: 0,
    totalTickets: 0,
    unpaidQuotes: 0,
    totalQuotes: 0,
    flights: 0,
    loading: true,
    error: null,
  });

  const loadDashStats = useCallback(async () => {
    setDashStats((s) => ({ ...s, loading: true, error: null }));
    try {
      const [tickets, quotes, flights] = await Promise.all([fetchTickets(), fetchQuotes(), fetchFlights()]);
      const openStatuses = new Set(['open', 'in_progress', 'waiting_customer']);
      const openTickets = tickets.filter((t) => openStatuses.has(t.status)).length;
      const unpaidQuotes = quotes.filter((q) => q.paymentStatus === 'unpaid').length;
      setDashStats({
        openTickets,
        totalTickets: tickets.length,
        unpaidQuotes,
        totalQuotes: quotes.length,
        flights: flights.length,
        loading: false,
        error: null,
      });
    } catch (e) {
      setDashStats((s) => ({
        ...s,
        loading: false,
        error: e instanceof Error ? e.message : 'Could not reach API',
      }));
    }
  }, []);

  useEffect(() => {
    void loadDashStats();
    const id = window.setInterval(() => void loadDashStats(), 30000);
    return () => window.clearInterval(id);
  }, [loadDashStats]);

  useEffect(() => {
    const fn = () => setStoreTick((t) => t + 1);
    window.addEventListener('skylux-storage', fn);
    return () => window.removeEventListener('skylux-storage', fn);
  }, []);

  const currentAgent = agents.find((a) => a.id === effectiveAgentId) ?? agents[0];
  const unreadCount = useMemo(() => {
    void storeTick;
    return loadNotifications().filter((n) => !n.read).length;
  }, [storeTick]);

  const headerSubtitle = useMemo(() => {
    if (!currentAgent) return authUsername ? `Session: ${authUsername}` : '';
    if (authUsername && authUsername !== currentAgent.name) {
      return `${currentAgent.nickname} · ${currentAgent.email} · Session: ${authUsername}`;
    }
    return `${currentAgent.nickname} · ${currentAgent.email}`;
  }, [authUsername, currentAgent]);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9', color: '#1e293b' }}>
      <aside
        style={{
          width: 'var(--sidebar-width)',
          background: 'linear-gradient(180deg, #1e1b4b 0%, #312e81 55%, #1e1b4b 100%)',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          height: '100vh',
          zIndex: 50,
          boxShadow: '4px 0 24px rgba(15, 23, 42, 0.18)',
        }}
      >
        <div style={{ padding: '1.75rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ background: 'var(--accent)', padding: '0.5rem', borderRadius: '0.75rem', boxShadow: '0 4px 14px rgba(59, 130, 246, 0.45)' }}>
            <LayoutDashboard size={24} />
          </div>
          <div>
            <h1 style={{ fontSize: '1.15rem', letterSpacing: '0.04em', fontWeight: '800', margin: 0, lineHeight: 1.2 }}>
              SKYLUX<span style={{ color: 'var(--accent)' }}>360</span>
            </h1>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.7rem', color: '#a5b4fc', fontWeight: 600 }}>Agent workspace</p>
          </div>
        </div>

        {!dashStats.loading && !dashStats.error && (
          <div
            style={{
              margin: '0 1rem 1rem',
              padding: '0.75rem 1rem',
              borderRadius: '0.75rem',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              fontSize: '0.75rem',
              lineHeight: 1.5,
              color: '#c7d2fe',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.35rem', color: 'white', fontWeight: 700 }}>
              <Activity size={14} /> Live from API
            </div>
            <div>Open tickets · {dashStats.openTickets}</div>
            <div>Unpaid quotes · {dashStats.unpaidQuotes}</div>
            <div>Flight rows · {dashStats.flights}</div>
          </div>
        )}
        {dashStats.error && (
          <div style={{ margin: '0 1rem 1rem', padding: '0.65rem 0.85rem', borderRadius: '0.65rem', background: 'rgba(248,113,113,0.15)', color: '#fecaca', fontSize: '0.72rem' }}>
            {dashStats.error}
          </div>
        )}

        <nav style={{ flex: 1, padding: '0 1rem', overflowY: 'auto' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <p
              style={{
                fontSize: '0.65rem',
                color: '#818cf8',
                padding: '0 0.75rem 0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontWeight: '800',
              }}
            >
              Navigation
            </p>
            <SidebarLink to={`/admin/agent/${effectiveAgentId}`} icon={<LayoutDashboard size={20} />} label="Overview" end />
            <SidebarLink to={`/admin/agent/${effectiveAgentId}/clients`} icon={<Users size={20} />} label="Clients" />
            <SidebarLink to={`/admin/agent/${effectiveAgentId}/flights`} icon={<Plane size={20} />} label="Flights" />
            <SidebarLink to={`/admin/agent/${effectiveAgentId}/flight-search`} icon={<SearchIcon size={20} />} label="Flight Search" />
            <SidebarLink to={`/admin/agent/${effectiveAgentId}/tickets`} icon={<Ticket size={20} />} label="Tickets" />
            <SidebarLink to={`/admin/agent/${effectiveAgentId}/emails`} icon={<Mail size={20} />} label="Email" />
            <SidebarLink to={`/admin/agent/${effectiveAgentId}/cms`} icon={<FileText size={20} />} label="CMS" />
            <SidebarLink
              to={`/admin/agent/${effectiveAgentId}/notifications`}
              icon={<Bell size={20} />}
              label="Notifications"
              badge={unreadCount > 0 ? String(unreadCount) : undefined}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <p
              style={{
                fontSize: '0.65rem',
                color: '#818cf8',
                padding: '0 0.75rem 0.5rem',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                fontWeight: '800',
              }}
            >
              Team
            </p>
            {canManageAgents && (
              <button
                type="button"
                onClick={() => navigate('/admin/agents')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  marginBottom: '0.5rem',
                  borderRadius: '0.65rem',
                  background: 'rgba(255,255,255,0.08)',
                  color: '#c7d2fe',
                  border: '1px dashed rgba(255,255,255,0.2)',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                <UserPlus size={16} /> Manage agents
              </button>
            )}
            <div style={{ display: 'grid', gap: '0.25rem' }}>
              {agentsLoading && agents.length === 0 && (
                <p style={{ fontSize: '0.75rem', color: '#a5b4fc', padding: '0.5rem 0.75rem', margin: 0 }}>Loading team…</p>
              )}
              {!agentsLoading && agents.length === 0 && (
                <button
                  type="button"
                  onClick={() => navigate('/admin/agents')}
                  style={{
                    padding: '0.65rem 0.75rem',
                    borderRadius: '0.65rem',
                    background: 'rgba(99, 102, 241, 0.25)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  + Add first agent
                </button>
              )}
              {agents.map((agent) => (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => navigate(`/admin/agent/${agent.id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    width: '100%',
                    padding: '0.55rem 0.75rem',
                    borderRadius: '0.65rem',
                    background: agent.id === effectiveAgentId ? 'rgba(99, 102, 241, 0.35)' : 'transparent',
                    color: agent.id === effectiveAgentId ? 'white' : '#a5b4fc',
                    border: '1px solid transparent',
                    transition: 'background 0.15s ease',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: agent.id === effectiveAgentId ? 'var(--accent)' : 'rgba(255,255,255,0.12)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      color: 'white',
                      flexShrink: 0,
                    }}
                  >
                    {agent.name.charAt(0)}
                  </div>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <p style={{ fontSize: '0.8125rem', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 0 }}>
                      {agent.name}
                    </p>
                    <p style={{ fontSize: '0.68rem', opacity: 0.75, margin: 0 }}>{agent.nickname}</p>
                  </div>
                  {agent.id === effectiveAgentId && (
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80', flexShrink: 0 }} aria-hidden />
                  )}
                </button>
              ))}
            </div>
          </div>
        </nav>

        <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <SidebarLink to={`/admin/agent/${effectiveAgentId}/settings`} icon={<Settings size={20} />} label="Settings" />
          <button
            type="button"
            onClick={() => {
              void logout().then(() => navigate('/login', { replace: true }));
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              width: '100%',
              padding: '0.65rem 0.75rem',
              borderRadius: '0.65rem',
              color: '#fecaca',
              background: 'transparent',
              marginTop: '0.35rem',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            <LogOut size={20} />
            Sign out
          </button>
        </div>
      </aside>

      <div style={{ flex: 1, marginLeft: 'var(--sidebar-width)', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header
          style={{
            minHeight: '72px',
            background: 'rgba(255, 255, 255, 0.92)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            padding: '0.65rem 2rem',
            position: 'sticky',
            top: 0,
            zIndex: 40,
            backdropFilter: 'blur(10px)',
          }}
        >
          <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '440px', minWidth: '180px' }}>
            <SearchIcon
              size={18}
              style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}
            />
            <input
              type="search"
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && globalSearch.trim()) {
                  navigate(`/admin/agent/${effectiveAgentId}/clients`);
                }
              }}
              placeholder="Search clients, routes… Enter to open directory"
              aria-label="Search admin data"
              style={{
                paddingLeft: '2.75rem',
                width: '100%',
                background: '#f1f5f9',
                border: '1px solid transparent',
                height: '44px',
                fontSize: '0.875rem',
                borderRadius: '0.75rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => navigate(`/admin/agent/${effectiveAgentId}/notifications`)}
              aria-label="Open notifications"
              style={{ color: 'var(--text-muted)', position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '0.35rem' }}
            >
              <Bell size={22} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    minWidth: '8px',
                    height: '8px',
                    background: 'var(--danger)',
                    borderRadius: '50%',
                    border: '2px solid white',
                  }}
                  aria-hidden
                />
              )}
            </button>
            <button type="button" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.35rem' }} aria-label="Help">
              <HelpCircle size={22} />
            </button>
            <div style={{ width: '1px', height: '32px', background: 'var(--border)' }} aria-hidden />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
              <div style={{ textAlign: 'right', minWidth: 0 }}>
                <p style={{ fontSize: '0.875rem', fontWeight: '700', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }}>
                  {currentAgent?.name ?? 'Agent'}
                </p>
                <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px' }}>
                  {headerSubtitle}
                </p>
              </div>
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, var(--accent), #6366f1)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '800',
                  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.35)',
                  flexShrink: 0,
                }}
              >
                {currentAgent?.name.charAt(0) ?? '?'}
              </div>
            </div>
          </div>
        </header>

        <main style={{ flex: 1, padding: 'clamp(1rem, 2vw, 2.5rem)', overflowY: 'auto' }}>
          <Outlet context={{ globalSearch, setGlobalSearch } satisfies AdminOutletContext} />
        </main>
      </div>
    </div>
  );
};

const SidebarLink = ({
  to,
  icon,
  label,
  badge,
  end = false,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  badge?: string;
  end?: boolean;
}) => (
  <NavLink
    to={to}
    end={end}
    style={({ isActive }) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      width: '100%',
      padding: '0.65rem 0.85rem',
      borderRadius: '0.65rem',
      background: isActive ? 'rgba(99, 102, 241, 0.9)' : 'transparent',
      color: isActive ? 'white' : '#c7d2fe',
      marginBottom: '0.2rem',
      textDecoration: 'none',
      transition: 'background 0.15s ease, color 0.15s ease',
      border: isActive ? '1px solid rgba(255,255,255,0.12)' : '1px solid transparent',
    })}
  >
    {icon}
    <span style={{ fontWeight: '600', flex: 1, fontSize: '0.9rem' }}>{label}</span>
    {badge && (
      <span
        style={{
          background: 'rgba(255,255,255,0.22)',
          color: 'white',
          fontSize: '0.65rem',
          padding: '0.12rem 0.45rem',
          borderRadius: '999px',
          fontWeight: '800',
        }}
      >
        {badge}
      </span>
    )}
  </NavLink>
);

export default AdminDashboard;
